import React, { useEffect } from 'react';

import {
  NOTIFICATION_DEFAULT_DELAY,
  NOTIFICATION_DEFAULT_ERROR_MESSAGE,
  NotificationColors,
  useNotificationContext,
} from 'application/providers/NotificationProvider';
import useGetCurrentBusinessSphere from 'application/hooks/useCurrentBusinessSphere';
import { useAccountCompanyId, useAccountCompanyStreamLibraryId } from 'application/hooks';

import { BusinessSpherePureType, StreamLibraryTypeFragment } from 'domain/api/graphql/generated';
import useGetStreamLibrary from 'domain/media/useGetStreamLibrary';

export type StreamLibrariesStateType = {
  currentStreamLibrary?: StreamLibraryTypeFragment | null;
  loading: boolean;
  currentBusinessSphere?: BusinessSpherePureType;
};

const StreamsLibraries = React.createContext({} as StreamLibrariesStateType);

export const useStreamLibrariesContext = () => React.useContext(StreamsLibraries);

const StreamLibrariesProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const notification = useNotificationContext();

  const companyId = useAccountCompanyId();
  const streamLibraryId = useAccountCompanyStreamLibraryId();

  const currentStreamLibrary = useGetStreamLibrary();
  const { currentBusinessSphere } = useGetCurrentBusinessSphere();

  const initialLoad = async () => {
    try {
      if (companyId && streamLibraryId) {
        await currentStreamLibrary.request(companyId, streamLibraryId);
      }
    } catch (e) {
      notification.showNotification({
        type: NotificationColors.ERROR,
        children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
        delay: NOTIFICATION_DEFAULT_DELAY,
      });
    }
  };

  useEffect(() => {
    initialLoad();
  }, [companyId, streamLibraryId]);

  return (
    <StreamsLibraries.Provider
      value={{
        currentStreamLibrary: currentStreamLibrary.streamLibrary,
        loading: currentStreamLibrary.loading,
        currentBusinessSphere,
      }}>
      {children}
    </StreamsLibraries.Provider>
  );
};

export default StreamLibrariesProvider;
